import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Grid from '@mui/material/Grid2'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import {
  TASK_PRIORITIES,
  TASK_PRIORITY_LABELS,
  TASK_STATUSES,
  TASK_STATUS_LABELS,
  createTaskSchema,
  type CreateTaskInput,
  type Paginated,
  type TaskDto,
} from '@lawyer/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import type { z } from 'zod'
import { api } from '../../lib/api'
import { formatDate } from '../../lib/format'
import { formResolver } from '../../lib/form'
import { qk } from '../../lib/queryClient'
import { useCaseOptions, useClientOptions, useProjectOptions, useUsers } from '../../hooks/useLookups'
import { ConfirmDialog } from '../common/ConfirmDialog'
import { DateField, EntitySelect, EnumSelect, type Option } from '../common/fields'
import { ErrorAlert, LoadingBlock } from '../common/Feedback'

type FormValues = z.input<typeof createTaskSchema>

const EMPTY: FormValues = {
  name: '',
  description: '',
  assignedUserId: '',
  startDate: '',
  dueDate: '',
  priority: 'MEDIUM',
  status: 'TODO',
  clientId: '',
  caseId: '',
  projectId: '',
  parentTaskId: '',
}

const toFormValues = (task: TaskDto): FormValues => ({
  name: task.name,
  description: task.description ?? '',
  assignedUserId: task.assignedUserId ?? '',
  startDate: task.startDate ?? '',
  dueDate: task.dueDate ?? '',
  priority: task.priority,
  status: task.status,
  clientId: task.clientId ?? '',
  caseId: task.caseId ?? '',
  projectId: task.projectId ?? '',
  parentTaskId: task.parentTaskId ?? '',
})

type Props = {
  open: boolean
  taskId?: string | null
  /** Pre-filled associations when opening from a case, client or project card. */
  defaults?: Partial<FormValues>
  onClose: () => void
}

export function TaskDialog({ open, taskId, defaults, onClose }: Props) {
  const queryClient = useQueryClient()
  const isEdit = Boolean(taskId)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const users = useUsers()
  const clients = useClientOptions()
  const cases = useCaseOptions()
  const projects = useProjectOptions()

  const existing = useQuery({
    queryKey: qk.task(taskId ?? ''),
    queryFn: () => api.get<TaskDto & { subtasks: TaskDto[] }>(`/tasks/${taskId}`),
    enabled: open && Boolean(taskId),
  })

  const parentCandidates = useQuery({
    queryKey: qk.tasks('parents'),
    queryFn: () =>
      api.get<Paginated<TaskDto>>('/tasks', {
        topLevelOnly: true,
        pageSize: 200,
        sortBy: 'createdAt',
        sortDir: 'desc',
      }),
    enabled: open,
  })

  const form = useForm<FormValues, unknown, CreateTaskInput>({
    resolver: formResolver(createTaskSchema),
    defaultValues: EMPTY,
  })

  const defaultsKey = JSON.stringify(defaults ?? {})

  useEffect(() => {
    if (!open) return
    if (taskId) {
      if (existing.data) form.reset(toFormValues(existing.data))
      return
    }
    form.reset({ ...EMPTY, ...defaults })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, taskId, existing.data, defaultsKey])

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['tasks'] })
    void queryClient.invalidateQueries({ queryKey: qk.myDay })
    if (taskId) void queryClient.invalidateQueries({ queryKey: qk.task(taskId) })
  }

  const save = useMutation({
    mutationFn: (values: CreateTaskInput) =>
      isEdit ? api.patch<TaskDto>(`/tasks/${taskId}`, values) : api.post<TaskDto>('/tasks', values),
    onSuccess: () => {
      invalidate()
      onClose()
    },
  })

  const remove = useMutation({
    mutationFn: () => api.del(`/tasks/${taskId}`),
    onSuccess: () => {
      invalidate()
      setConfirmDelete(false)
      onClose()
    },
  })

  const userOptions: Option[] = (users.data ?? []).map((u) => ({ id: u.id, label: u.name }))
  const clientOptions: Option[] = (clients.data ?? []).map((c) => ({ id: c.id, label: c.displayName }))
  const caseOptions: Option[] = (cases.data ?? []).map((c) => ({ id: c.id, label: c.name }))
  const projectOptions: Option[] = (projects.data ?? []).map((p) => ({ id: p.id, label: p.displayName }))
  const parentOptions: Option[] = (parentCandidates.data?.items ?? [])
    .filter((t) => t.id !== taskId)
    .map((t) => ({ id: t.id, label: t.name }))

  const subtasks = existing.data?.subtasks ?? []

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <form onSubmit={form.handleSubmit((values) => save.mutate(values))} noValidate>
          <DialogTitle>{isEdit ? 'עריכת משימה' : 'משימה חדשה'}</DialogTitle>

          <DialogContent dividers>
            {isEdit && existing.isPending ? <LoadingBlock /> : null}
            {save.error ? <ErrorAlert error={save.error} /> : null}

            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid size={12}>
                <TextField
                  label="שם המשימה"
                  required
                  autoFocus
                  {...form.register('name')}
                  error={Boolean(form.formState.errors.name)}
                  helperText={form.formState.errors.name?.message}
                />
              </Grid>
              <Grid size={12}>
                <TextField label="תיאור" multiline minRows={2} {...form.register('description')} />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <EntitySelect
                  control={form.control}
                  name="assignedUserId"
                  label="עובד מטפל"
                  options={userOptions}
                  loading={users.isPending}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <EnumSelect
                  control={form.control}
                  name="status"
                  label="סטטוס"
                  options={TASK_STATUSES.map((s) => ({ id: s, label: TASK_STATUS_LABELS[s] }))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <EnumSelect
                  control={form.control}
                  name="priority"
                  label="דחיפות"
                  options={TASK_PRIORITIES.map((p) => ({ id: p, label: TASK_PRIORITY_LABELS[p] }))}
                />
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <DateField control={form.control} name="startDate" label="תאריך התחלה" />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DateField control={form.control} name="dueDate" label="תאריך יעד" />
              </Grid>

              <Grid size={12}>
                <Typography variant="overline" color="text.secondary">
                  שיוך (אופציונלי)
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <EntitySelect
                  control={form.control}
                  name="clientId"
                  label="לקוח"
                  options={clientOptions}
                  loading={clients.isPending}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <EntitySelect
                  control={form.control}
                  name="caseId"
                  label="תיק"
                  options={caseOptions}
                  loading={cases.isPending}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <EntitySelect
                  control={form.control}
                  name="projectId"
                  label="פרויקט"
                  options={projectOptions}
                  loading={projects.isPending}
                />
              </Grid>

              <Grid size={12}>
                <EntitySelect
                  control={form.control}
                  name="parentTaskId"
                  label="משימת אב"
                  options={parentOptions}
                  loading={parentCandidates.isPending}
                  helperText="בחירת משימת אב הופכת את המשימה לתת־משימה"
                />
              </Grid>

              {subtasks.length ? (
                <Grid size={12}>
                  <Typography variant="overline" color="text.secondary">
                    תת־משימות ({subtasks.length})
                  </Typography>
                  <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                    {subtasks.map((subtask) => (
                      <Typography key={subtask.id} variant="body2">
                        {subtask.name}
                        {subtask.assignedUserName ? ` — ${subtask.assignedUserName}` : ''}
                        {subtask.dueDate ? ` · ${formatDate(subtask.dueDate)}` : ''}
                      </Typography>
                    ))}
                  </Stack>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    מחיקת משימת אב מוחקת גם את תת־המשימות שלה.
                  </Alert>
                </Grid>
              ) : null}
            </Grid>
          </DialogContent>

          <DialogActions>
            {isEdit ? (
              <Button color="error" onClick={() => setConfirmDelete(true)} sx={{ mr: 'auto' }}>
                מחיקה
              </Button>
            ) : null}
            <Button onClick={onClose}>ביטול</Button>
            <Button type="submit" variant="contained" disabled={save.isPending}>
              {save.isPending ? 'שומר…' : 'שמירה'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <ConfirmDialog
        open={confirmDelete}
        title="מחיקת משימה"
        message={
          subtasks.length
            ? `המשימה כוללת ${subtasks.length} תת־משימות שיימחקו גם הן. להמשיך?`
            : 'המשימה תימחק לצמיתות. להמשיך?'
        }
        confirmLabel="מחיקה"
        busy={remove.isPending}
        onConfirm={() => remove.mutate()}
        onClose={() => setConfirmDelete(false)}
      />
    </>
  )
}
